import React from 'react';
import classNames from 'classnames';

type TypographyVariant =
  | 'header'
  | 'subheader'
  | 'title'
  | 'subtitle'
  | 'body'
  | 'numbers'
  | 'caption'
  | 'code'
  | 'link'
  | 'title-body'
  | 'title-caption';

type Whitespace = 'preserve';
type WhitespaceWithAnyString = Exclude<React.CSSProperties['whiteSpace'], undefined>;
type CSSWhitespace = {
  [WhitespaceValue in WhitespaceWithAnyString]: string & {} extends WhitespaceValue
    ? never
    : WhitespaceValue;
}[WhitespaceWithAnyString];

const WHITESPACE_TO_STYLE = {
  preserve: 'pre',
} satisfies Record<Whitespace, CSSWhitespace>;

interface TypographyProps {
  children: React.ReactNode;
  variant?: TypographyVariant;
  appearance?: 'error';
  tag?: 'div' | 'span';
  className?: string;
  color?: string;
  /** Used to align warpped/multiline strings */
  textAlign?: 'left' | 'center' | 'right';
  whiteSpace?: Whitespace;
  /** Native tooltip text; useful to reveal the full value when the text is visually truncated. */
  title?: string;
  ['data-testid']?: string;
  ['aria-label']?: string;
}

export function Typography({
  children,
  variant: typography = 'body',
  appearance,
  tag: Tag = 'div',
  className,
  ['data-testid']: dataTestId,
  ['aria-label']: ariaLabel,
  color,
  textAlign,
  whiteSpace,
  title,
}: TypographyProps): JSX.Element {
  return (
    <Tag
      className={classNames(`text-style-${typography}`, className, {
        'text-style-error': appearance === 'error',
      })}
      data-testid={dataTestId}
      aria-label={ariaLabel}
      title={title}
      style={{ color, textAlign, whiteSpace: whiteSpace && WHITESPACE_TO_STYLE[whiteSpace] }}
    >
      {children}
    </Tag>
  );
}
